/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Option,
  Select,
  Space,
  Stack,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useWhyDidYouUpdate } from 'src/utils/hooks';
import {
  KfContainRule,
  KfContainTypeEnum,
  KfFileTypeEnum,
} from '@Policy/utils/types';
import { FormLabel } from '@Components/Common/FormComponent';
import EditableList from '@Components/EditableList';
import { generalExport } from '@Policy/utils/fns';
import ImportModal from '@Policy/AdvancedThreatProtection/PolicyTemplate/Components/ImportModal';

const RuleModal = (props: {
  isVisible: boolean;
  onClose: () => void;
  data: KfContainRule;
  // set the tempVars.slfExceptionUsers to slfExceptionUsers
  // set the tempVars.slfExceptionPaths to slfExceptionPaths
  // clear tempVars slfExceptionUsers and slfExceptionPaths
  saveConfig: (rules: KfContainRule[]) => void;
}) => {
  const { isVisible, onClose, saveConfig, data } = props;
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  // TODO: check keywordExtractionCount
  // const contextStore = usePolicyContext();
  // const contextSelector = storeSelector(contextStore);

  const [kfFileType, setKfFileType] = React.useState<KfFileTypeEnum[]>([
    data.kfFileType,
  ]);
  const handleKfFileTypeCheck = React.useCallback(
    (fileType: KfFileTypeEnum) => {
      if (kfFileType.includes(fileType)) {
        setKfFileType(kfFileType.filter((item) => item !== fileType));
      } else {
        setKfFileType([...kfFileType, fileType]);
      }
    },
    [kfFileType]
  );
  const [tmKeywords, setTmKeywords] = React.useState(() => {
    const tmKeywords = data.tmKeywords.map((keyword: string) => ({
      value: keyword,
      name: keyword,
    }));
    return tmKeywords;
  });
  const importKeyword = React.useCallback(
    (entries: string[]) => {
      const originTmKeywords = tmKeywords.map((item) => item.value);
      const newTmKeywords: React.SetStateAction<
        { value: string; name: string }[]
      > = [];
      entries.forEach((entry) => {
        if (!originTmKeywords.includes(entry)) {
          newTmKeywords.push({ value: entry, name: entry });
        }
      });
      setTmKeywords(newTmKeywords);
    },
    [tmKeywords]
  );
  const [kfContainType, setKfContainType] = React.useState(data.kfContainType);
  const handleDeleteTmKeyword = React.useCallback(
    (keyword: string) => {
      const newTmKeywords = tmKeywords.filter((item) => item.value !== keyword);
      setTmKeywords(newTmKeywords);
    },
    [tmKeywords]
  );
  const handleAddTmKeyword = React.useCallback(
    (keyword: string) => {
      const value = keyword.trim();
      if (tmKeywords.map((item) => item.value).includes(value)) {
        return;
      } else {
        const newTmKeywords = [
          ...tmKeywords,
          { value: keyword, name: keyword },
        ];
        setTmKeywords(newTmKeywords);
      }
    },
    [tmKeywords]
  );
  const onSave = React.useCallback(() => {
    const newData = kfFileType.map((item) => ({
      ...data,
      kfFileType: item,
      tmKeywords: tmKeywords.map((item) => item.value),
      kfContainType,
    }));
    saveConfig(newData);
    //clear the modal state
    setKfFileType([KfFileTypeEnum.Text]);
    setKfContainType(KfContainTypeEnum.Contain);
    setTmKeywords([]);
    onClose();
  }, [kfFileType, tmKeywords, kfContainType]);
  const saveDisabled = React.useMemo(() => {
    return !tmKeywords.length;
  }, [tmKeywords]);
  // keywords export
  const handleKeywordExport = React.useCallback((entries: any) => {
    generalExport({
      entries: entries.map((entry: any) => entry.value),
      name: 'keyword-list',
    });
  }, []);
  // keywords import
  const [isKeywordImportModalVisible, setIsKeywordImportModalVisible] =
    React.useState(false);
  const handleKeywordImportModalClose = React.useCallback(() => {
    setIsKeywordImportModalVisible(false);
  }, []);
  const handleKeywordImportModalOpen = React.useCallback(() => {
    setIsKeywordImportModalVisible(true);
  }, []);
  const handleKeywordImport = React.useCallback((entries: any) => {
    console.log('import entries', entries);
    importKeyword(entries as string[]);
    setIsKeywordImportModalVisible(false);
  }, []);
  useWhyDidYouUpdate('KeywordExtraction RuleModal', {
    ...props,
  });
  React.useEffect(() => {
    setKfFileType([data.kfFileType]);
    setKfContainType(data.kfContainType);
    setTmKeywords(
      data.tmKeywords.map((keyword: string) => ({
        value: keyword,
        name: keyword,
      }))
    );
  }, [data]);

  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={isVisible}
      onClose={onClose}
      size="auto"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent backgroundColor={colorStyle.background.secondary}>
        <ModalHeader></ModalHeader>

        <ModalBody ml="24px" mt="16px" mb="16px">
          <Flex mb="16px" alignItems="flex-start">
            <FormLabel
              label={t('policy.keywordExtraction.label.fileFormat')}
              withColon
            />
            <Stack
              direction="column"
              mt="10px"
              mb="10px"
              spacing="1x"
              shouldWrapChildren
            >
              <Checkbox
                checked={kfFileType.includes(KfFileTypeEnum.Text)}
                onChange={() => handleKfFileTypeCheck(KfFileTypeEnum.Text)}
              >
                {t('policy.keywordExtraction.option.text')}
              </Checkbox>
              <Checkbox
                checked={kfFileType.includes(KfFileTypeEnum.AdobePdf)}
                onChange={() => handleKfFileTypeCheck(KfFileTypeEnum.AdobePdf)}
              >
                {t('policy.keywordExtraction.option.pdf')}
              </Checkbox>
              <Checkbox
                checked={kfFileType.includes(KfFileTypeEnum.MicrosoftDoc)}
                onChange={() =>
                  handleKfFileTypeCheck(KfFileTypeEnum.MicrosoftDoc)
                }
              >
                {t('policy.keywordExtraction.option.microsoftDoc')}
              </Checkbox>
            </Stack>
          </Flex>
          <Flex mb="16px" alignItems="flex-start">
            <FormLabel
              label={t('policy.keywordExtraction.label.condition')}
              withColon
            />
            <Select
              value={kfContainType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setKfContainType(Number(e.target.value));
              }}
            >
              <Option
                value={KfContainTypeEnum.Contain}
                key={KfContainTypeEnum.Contain}
              >
                {t('policy.keywordExtraction.option.contain')}
              </Option>
              <Option
                value={KfContainTypeEnum.NotContain}
                key={KfContainTypeEnum.NotContain}
              >
                {t('policy.keywordExtraction.option.notContain')}
              </Option>
            </Select>
          </Flex>
          <EditableList
            list={tmKeywords}
            onDelete={handleDeleteTmKeyword}
            onAdd={handleAddTmKeyword}
            onImport={handleKeywordImportModalOpen}
            onExport={handleKeywordExport}
            description=""
            width="678px"
            size={5}
            limit={-1}
            disabled={false}
            placeholder=" "
          />
          <ImportModal
            isVisible={isKeywordImportModalVisible}
            onClose={handleKeywordImportModalClose}
            onImport={handleKeywordImport}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            variant="primary"
            minWidth="20x"
            onClick={onSave}
            disabled={saveDisabled}
          >
            {t('policy.keywordExtraction.button.addToRuleList')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(RuleModal);
